/**
 * This file contains authentication parameters. Contents of this file is roughly the same across other MSAL.js
 * libraries. These parameters are used to initialize Angular and MSAL Angular configurations in in app.module.ts file.
 */

import { MsalGuardConfiguration, MsalInterceptorConfiguration } from "@azure/msal-angular";
import { BrowserCacheLocation, Configuration, InteractionType, LogLevel } from "@azure/msal-browser";

export const azureConfig = {
  tenantId: "2598639a-d083-492d-bdbe-f1dd8066b03a",
  clientId: "868435ef-6e8c-4220-97ca-5bcb9963f528",
};

const isIE = window.navigator.userAgent.includes("MSIE ") || window.navigator.userAgent.includes("Trident/");

/**
 * Configuration object to be passed to MSAL instance on creation. For a full list of MSAL.js configuration parameters,
 * visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: azureConfig.clientId,
    authority: `https://login.microsoftonline.com/${azureConfig.tenantId}`,
    redirectUri: "/auth",
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: isIE,
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        console.log(message);
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
    tokenRenewalOffsetSeconds: 300,
    windowHashTimeout: 5000,
    iframeHashTimeout: 5000,
    loadFrameTimeout: 5000,
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const msalInterceptorConfig: MsalInterceptorConfiguration = {
  interactionType: InteractionType.Redirect,
  protectedResourceMap: new Map([
    // Microsoft Graph
    ["https://graph.microsoft.com/v1.0/me/**", ["user.read"]],

    // Localhost APIs
    ["/api/**", [`${azureConfig.clientId}/.default`]],
    ["http://127.0.0.1:8000/**", [`${azureConfig.clientId}/.default`]], // send token to api-ml services
    ["http://127.0.0.1:8001/**", [`${azureConfig.clientId}/.default`]], // send token to scraping services

    // API-ML services
    ["https://api-ml-dev.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://api-ml-exp.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://api-ml-int.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://api-ml-prod.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],

    // Scraping services
    ["https://scraping-dev.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://scraping-exp.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://scraping-int.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
    ["https://scraping-prod.rts-digital-ia.ch/**", [`${azureConfig.clientId}/.default`]],
  ]),
};

/**
 * Scopes you add here will be prompted for user consent during sign-in. By default, MSAL.js will add OIDC scopes
 * (openid, profile, email) to any login request. For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const msalGuardConfig: MsalGuardConfiguration = {
  interactionType: InteractionType.Redirect,
  authRequest: {
    scopes: ["user.read"],
  },
};
